// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  IGalleryGetAllAttrs,
  IGalleryGetAttrs,
  IGallerySaveAttrs,
  IGalleryGetGalleryImagesAttrs,
  IGalleryUpdateImageAttrs,
  IGalleryDeleteImageAttrs,
} from 'interfaces/back-office/content-management/gallery/gallery/gallery-view.interface'
import { IGalleryImage } from 'interfaces/back-office/content-management/gallery/gallery'

const useGallery = () => {
  // Hook
  const {
    gallery: {
      gallery_isLoading,
      gallery_isSaveLoading,
      gallery_isImageLoading,
      gallery_galleries,
      gallery_detail,
      gallery_images,
      gallery_CLEAR_GALLERIES,
      gallery_CLEAR_GALLERY,
      gallery_CLEAR_GALLERY_IMAGES,
      gallery_SET_NEW_IMAGE,
      gallery_DELETE_GALLERY_IMAGE,
      gallery_fetchGalleries: galleryFetchList,
      gallery_fetchGallery: galleryFetch,
      gallery_saveGallery: gallerySave,
      gallery_fetchGalleryImages: galleryFetchImages,
      gallery_updateImage: galleryUpdateImage,
      gallery_deleteImage: galleryDeleteImage,
    },
  } = useStores()

  /**
   * @description Fetch Gallery LIst
   *
   * @param {IGalleryGetAllAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const gallery_fetchGalleries = useCallback((payload: IGalleryGetAllAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      galleryFetchList({
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Fetch Gallery Detail
   *
   * @param {IGalleryGetAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const gallery_fetchGallery = useCallback((payload: IGalleryGetAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      galleryFetch({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Save gallery
   *
   * @param {IGallerySaveAttrs} payload
   *
   * @return {Promise<IGalleryImage|void>} Promise<IGalleryImage|void>
   */
  const gallery_saveGallery = useCallback((payload: IGallerySaveAttrs): Promise<IGalleryImage | void> => {
    return new Promise((resolve, reject) => {
      gallerySave({
        params: payload.params,
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Fetch gallery image list
   *
   * @param {IGalleryGetGalleryImagesAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const gallery_fetchGalleryImages = useCallback((payload: IGalleryGetGalleryImagesAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      galleryFetchImages({
        params: payload.params,
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Update gallery image
   *
   * @param {IGalleryUpdateImageAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const gallery_updateImage = useCallback((payload: IGalleryUpdateImageAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      galleryUpdateImage({
        params: payload.params,
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Delete gallery image
   *
   * @param {IGalleryDeleteImageAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const gallery_deleteImage = useCallback((payload: IGalleryDeleteImageAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      galleryDeleteImage({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  return {
    gallery_isLoading,
    gallery_isSaveLoading,
    gallery_isImageLoading,
    gallery_galleries,
    gallery_detail,
    gallery_images,
    gallery_CLEAR_GALLERIES,
    gallery_CLEAR_GALLERY,
    gallery_CLEAR_GALLERY_IMAGES,
    gallery_SET_NEW_IMAGE,
    gallery_DELETE_GALLERY_IMAGE,
    gallery_fetchGalleries,
    gallery_fetchGallery,
    gallery_saveGallery,
    gallery_fetchGalleryImages,
    gallery_updateImage,
    gallery_deleteImage,
  }
}

export { useGallery }
